import { crmStatusLabels, findCrmSubStatusDefinition, getContactOriginLabel } from "../../../../../@types/sharedSchema";
import { contactShowsBuyerSurfaces } from "../../../../../utils/contacts/contactSides";
import type { CrmStatus } from "../../../../../@types/sharedSchema";
import type { CrmContactHolder } from "../../../../../@types/webotServer";

/**
 * THE AGENCY VIEW OF ONE PERSON, as data — pure, no React, no fetch.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * THIS FILE IS THE SINGLE ENFORCEMENT POINT FOR THE `pool` TIER
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Every view-model below is built here and the components render ONLY these — never a raw
 * `CrmContactHolder`. That is what makes the redaction reviewable: a full-only field can
 * reach a `basic` screen in exactly one way, which is a `pool === "full"` branch in this
 * file being wrong, and there is a test that builds a basic model from a holder carrying
 * every full-only field and asserts none of them survives.
 *
 * The server is the actual enforcement — under `basic` it does not SELECT the CRM axes at
 * all, so they are absent from the payload rather than nulled. This file is the second
 * lock, and the one a reviewer can read in a minute.
 *
 * ── The counting rule, which is easy to get wrong in the same way twice ──────────
 *
 * ⚠️ EVERY COUNT HERE INCLUDES THE CALLER'S OWN COPY. The holders endpoint counts every
 * holder in scope and the caller is in scope; the שמור אצל list shows all of them, so its
 * header must count all of them. The "אנשי הקשר שלי" avatar STACK is the surface that
 * subtracts one — its set deliberately excludes the row's own owner — and feeding this
 * model that stack's number would put a "+1" on a person held by exactly the caller and
 * two colleagues, pointing at a third agent who does not exist. A count and the list it
 * labels must be in the same unit.
 */

export type AgencyPool = "none" | "basic" | "full";

// ───────────────────────────────────────────────────────────────────────────────
// Holder rows
// ───────────────────────────────────────────────────────────────────────────────

export type AgencyHolderView = {
  ownerUserId: string;
  ownerName: string | null;
  ownerAvatarUrl: string | null;
  /** The name on THAT colleague's copy — which is routinely different from the caller's. */
  name: string | null;
  isMine: boolean;
  /** True for the earliest `createdTime` in the list — the "got there first" mark. */
  isFirst: boolean;
  createdTime: string | null;
  lastChangeTime: string | null;
  // ── `pool: "full"` only. Absent, never null, at basic. ────────────────────────
  statusLabel?: string | null;
  isHot?: boolean;
  originLabel?: string | null;
  criteria?: string[];
  tags?: string[];
  notes?: string | null;
};

/**
 * `lastChangeTime` with a fallback to the deprecated raw `updatedTime`.
 *
 * The fallback exists ONLY to make the deploy order of the server and this app
 * irrelevant: for one release a client that has shipped ahead of the server would
 * otherwise render an empty "שינוי אחרון" on every row. Delete it — and the
 * `updatedTime` field with it — once the server change is out.
 */
const lastChangeOf = (holder: CrmContactHolder): string | null =>
  holder.lastChangeTime ?? holder.updatedTime ?? null;

const parseMs = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : null;
};

/** The earliest holder by `createdTime`. Null when nobody has one. */
const earliestOwnerId = (holders: readonly CrmContactHolder[]): string | null => {
  let bestId: string | null = null;
  let bestMs = Number.POSITIVE_INFINITY;
  holders.forEach((holder) => {
    const ms = parseMs(holder.createdTime);
    if (ms === null || ms >= bestMs) return;
    bestMs = ms;
    bestId = holder.ownerUserId;
  });
  return bestId;
};

/**
 * Budget · customer types · cities — the one-line "what they are looking for".
 *
 * ⚠️ THE SEARCH TERMS ARE DROPPED FOR A PURE SELLER, and the test is PER HOLDER.
 *
 * Same rule, same predicate and same reject as the פרטי חיפוש panel (2026-08-25: a
 * seller/landlord who is not also a buyer/renter has no search profile to show, "even if
 * defined"). It applies here too because this caption is assembled from exactly those
 * fields — a מוכר carrying a budget and a city from an old import printed
 * `₪2,500,000 · מוכר · תל אביב`, i.e. somebody's stale import rendered as that person's
 * requirements, on the one surface built for comparing colleagues' records side by side.
 *
 * PER HOLDER rather than per contact, which is the whole reason this could not simply
 * inherit the panel's gate: every colleague holds their OWN copy with their own
 * `customerTypes`, so the same person can be a seller in one agent's book and a buyer in
 * another's — and it is that disagreement the comparison exists to show.
 *
 * The customer-TYPES term survives the gate on purpose. It is identity ("this colleague
 * has them as a מוכר"), not criteria, and it is the term that explains why the rest of
 * the line is short.
 */
const criteriaOf = (holder: CrmContactHolder, formatBudget: (value: number | null | undefined) => string): string[] => {
  const parts: string[] = [];
  const showsSearchTerms = contactShowsBuyerSurfaces({ customerTypes: holder.customerTypes });
  if (showsSearchTerms) {
    const sale = formatBudget(holder.budget);
    const rent = formatBudget(holder.budgetRent);
    if (sale) parts.push(sale);
    else if (rent) parts.push(`שכ״ד ${rent}`);
  }
  if (holder.customerTypes?.length) parts.push(holder.customerTypes.join(", "));
  if (showsSearchTerms && holder.cities?.length) parts.push(holder.cities.join(", "));
  return parts;
};

export function buildHolderViews(
  holders: readonly CrmContactHolder[],
  myUserId: string,
  pool: AgencyPool,
  formatBudget: (value: number | null | undefined) => string
): AgencyHolderView[] {
  const firstOwner = earliestOwnerId(holders);

  const views = holders.map((holder) => {
    const base: AgencyHolderView = {
      ownerUserId: holder.ownerUserId,
      ownerName: holder.ownerName,
      ownerAvatarUrl: holder.ownerAvatarUrl,
      name: holder.name,
      isMine: holder.ownerUserId === myUserId,
      isFirst: holder.ownerUserId === firstOwner,
      createdTime: holder.createdTime,
      lastChangeTime: lastChangeOf(holder)
    };

    // ⚠️ THE ONLY PLACE A FULL-ONLY FIELD IS READ. Everything above this line is the
    // declared basic floor; everything below is invisible to a basic caller because the
    // server never sent it — and would be invisible even if it had.
    if (pool !== "full") return base;

    const subStatus = holder.crmSubStatus ? findCrmSubStatusDefinition(holder.crmSubStatus)?.label : null;
    const status = holder.crmStatus ? crmStatusLabels[holder.crmStatus as CrmStatus] : null;

    return {
      ...base,
      statusLabel: status ? (subStatus ? `${status} · ${subStatus}` : status) : null,
      isHot: Boolean(holder.crmIsHot),
      // LABELLED, never raw. Every system writer stamps a canonical English value
      // (`AI call transcription`, `webhook`, …); printing it verbatim is how one contact
      // reads "Homely with webot AI" while every other reads Hebrew.
      originLabel: holder.contactOrigin ? getContactOriginLabel(holder.contactOrigin) : null,
      criteria: criteriaOf(holder, formatBudget),
      tags: holder.tags ?? [],
      notes: holder.additionalNotes ?? null
    };
  });

  /**
   * MINE FIRST, then earliest, then by name.
   *
   * Mine first because the comparison this panel exists for is mine-against-theirs, and
   * it should be the first thing on screen. Then `createdTime` ASCENDING with nulls last,
   * so the list reads as the order the agency actually acquired this person — which is
   * what makes the summary's "got there first" claim checkable against the rows under it
   * rather than a number the reader has to take on trust.
   */
  return views.sort((a, b) => {
    if (a.isMine !== b.isMine) return a.isMine ? -1 : 1;
    const aMs = parseMs(a.createdTime);
    const bMs = parseMs(b.createdTime);
    if (aMs !== bMs) {
      if (aMs === null) return 1;
      if (bMs === null) return -1;
      return aMs - bMs;
    }
    return (a.ownerName ?? a.ownerUserId).localeCompare(b.ownerName ?? b.ownerUserId);
  });
}

// ───────────────────────────────────────────────────────────────────────────────
// The summary
// ───────────────────────────────────────────────────────────────────────────────

export type AgencySummary = {
  /** Who created their copy first, and when. Null when no holder carries a create time. */
  first: { ownerName: string; createdTime: string } | null;
  /**
   * ⚠️ TRUE when the holder list was CAPPED, which makes `first` a claim about the copies
   * ON SCREEN rather than about the agency.
   *
   * The cap keeps the 20 most recently TOUCHED copies — precisely the ordering least
   * likely to contain the earliest one — so this is not a theoretical caveat and the panel
   * must say so beside the claim rather than assert something it cannot know.
   */
  firstIsAmongShownOnly: boolean;
  /** The lead source on the FIRST holder's copy. `pool: "full"` only; null otherwise. */
  originLabel: string | null;
};

export function buildAgencySummary(
  views: readonly AgencyHolderView[],
  totalCount: number
): AgencySummary {
  const withCreate = views.filter((view) => view.createdTime);
  // `views` is sorted mine-first, so the earliest is found by value rather than by taking
  // the head — the two disagree whenever the caller is not the first holder, which is the
  // interesting case.
  const first = withCreate.reduce<AgencyHolderView | null>((best, view) => {
    if (!best) return view;
    return new Date(view.createdTime as string).getTime() < new Date(best.createdTime as string).getTime() ? view : best;
  }, null);

  return {
    first: first ? { ownerName: first.ownerName ?? first.ownerUserId, createdTime: first.createdTime as string } : null,
    firstIsAmongShownOnly: totalCount > views.length,
    originLabel: first?.originLabel ?? null
  };
}

// ───────────────────────────────────────────────────────────────────────────────
// The timeline
// ───────────────────────────────────────────────────────────────────────────────

/**
 * The shape a timeline row needs. Structural rather than an import, so this pure module
 * does not depend on the timeline tab — a file another agent owns.
 *
 * ⚠️ IT IS DELIBERATELY IDENTICAL TO `TimelineEntry`, field for field, INCLUDING
 * `isBlurred` being REQUIRED. The merge below now feeds the real timeline component
 * (`TimelineSurface`) rather than a bespoke list, so entries cross this boundary in both
 * directions — the tab's normalized entries go in, and the synthetic rows come out and are
 * rendered by `TimelineItemRow`. An optional field here would make one of those two
 * assignments illegal and force a cast at the seam, which is exactly the place a cast
 * would stop a genuine shape drift from being caught.
 */
export type AgencyTimelineEntry = {
  key: string;
  actionType: string;
  description: string;
  time: Date | null;
  timeMs: number;
  user: { userId: string; userName: string; userAvatar: string };
  isBlurred: boolean;
};

/** The synthetic action type. Deliberately not a member of the server's closed union. */
export const CONTACT_CREATED_ACTION = "contact-created";

/**
 * What a "נוצר כרטיס" row is made from: one holder's copy, as the agency roster or the contact's
 * own dossier names it (`timelineData.createdRowSources`).
 */
export type CreatedRowSource = Pick<AgencyHolderView, "ownerUserId" | "ownerName" | "ownerAvatarUrl" | "createdTime">;

/**
 * One "נוצר כרטיס אצל X" entry per holder — DERIVED, not an audit record.
 *
 * ⚠️ IT MUST NOT READ AS AN AUDIT TRAIL. There is no create event anywhere: the contact
 * timeline's action types are a closed seven-value union that has no such member, and the
 * CRM audit table declares a `create` action that no code path has ever written. This is
 * `createdTime` turned into a row so that "when did each colleague get this person" is
 * visible on the same axis as the calls and meetings — nothing more. It carries no "how"
 * and no second actor.
 *
 * A holder with NO `createdTime` is SKIPPED rather than dated 0. An entry at the epoch
 * sorts into the timeline's "ללא תאריך" bucket, where it reads as corrupted data instead
 * of as "we do not know" — and "we do not know" is better said by the row's absence.
 */
export function buildCreatedEntries(views: readonly CreatedRowSource[]): AgencyTimelineEntry[] {
  return views
    .filter((view) => view.createdTime)
    .map((view) => {
      const time = new Date(view.createdTime as string);
      return {
        // NAMESPACED, so it can never collide with the server's own `${i}:${j}:…` keys.
        key: `synthetic:created:${view.ownerUserId}`,
        actionType: CONTACT_CREATED_ACTION,
        description: "",
        time,
        timeMs: time.getTime(),
        user: {
          userId: view.ownerUserId,
          userName: view.ownerName ?? view.ownerUserId,
          userAvatar: view.ownerAvatarUrl ?? ""
        },
        isBlurred: false
      };
    });
}

/**
 * The agency timeline: the contact's own events, optionally INTERSECTED with the holder
 * set, plus the synthetic create rows.
 *
 * ⚠️ THE INTERSECTION IS A SCOPE FIX, NOT A TIDY-UP — and it belongs to exactly one of the
 * two surfaces that now call this. The holders request is scoped to ONE agency; the contact
 * dossier is not — it unions every organization the caller belongs to and queries the
 * collection group unscoped. A panel TITLED AFTER ONE AGENCY must therefore drop the rest,
 * or it names a colleague from a different agency and disagrees with the list of holders
 * printed directly above it.
 *
 * ⚠️ AND IT IS EXACTLY WRONG ON THE CONTACT PAGE, which is why `restrictToHolders` exists.
 * That tab is not about an agency; it is every event the server was willing to return for
 * this person. Filtering it against one agency's roster would DELETE rows a realtor has
 * been reading for months — every event from a second agency, and every event from a
 * colleague who has since left the roster. Narrowing an existing surface is not a
 * side-effect a shared helper gets to have.
 *
 * The default is the narrow one on purpose: a caller that forgets to say gets the scoped
 * answer, and the one place that wants the whole union asks for it in writing.
 *
 * The caller is always admitted, because their own events belong on their own screen even
 * when their copy is somehow absent from the holders payload.
 */
export function mergeAgencyTimeline(
  entries: readonly AgencyTimelineEntry[],
  views: readonly CreatedRowSource[],
  myUserId: string,
  options: { restrictToHolders?: boolean } = {}
): AgencyTimelineEntry[] {
  const inScope = new Set<string>([myUserId, ...views.map((view) => view.ownerUserId)]);
  const scoped =
    options.restrictToHolders === false
      ? entries
      : entries.filter((entry) => inScope.has(entry.user.userId));
  return [...scoped, ...buildCreatedEntries(views)].sort((a, b) => b.timeMs - a.timeMs);
}

/**
 * The most recent thing that actually HAPPENED with this person, for the summary line.
 *
 * ⚠️ THREE EXCLUSIONS, EACH ONE A MEASURED TRAP:
 *
 *  1. FUTURE EVENTS ARE NOT ACTIVITY. Meetings are timestamped with their scheduled
 *     START and the query has no upper bound, so a meeting booked for next week sorts
 *     FIRST — and a summary line reading "last activity: next Tuesday" is nonsense.
 *  2. `message-sent` IS A BULK CAMPAIGN, not a conversation. Those entries come from the
 *     bulk-messaging log; counting a blast as the last activity would say a colleague is
 *     working a lead they mailshot.
 *  3. THE SYNTHETIC CREATE ROWS ARE NOT ACTIVITY either — they are the list above,
 *     restated. Including them would make every person show "last activity" equal to
 *     whenever the last colleague filed them.
 *
 * Falls back to the newest remaining entry of ANY type rather than to nothing, because a
 * note or a signature IS activity even though it is not a conversation.
 */
const CONVERSATIONAL = new Set(["call", "meeting"]);

export function findLastActivity(
  entries: readonly AgencyTimelineEntry[],
  now: number
): AgencyTimelineEntry | null {
  const past = entries.filter(
    (entry) => entry.timeMs > 0 && entry.timeMs <= now && entry.actionType !== CONTACT_CREATED_ACTION
  );
  return past.find((entry) => CONVERSATIONAL.has(entry.actionType)) ?? past[0] ?? null;
}
